import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Settings, Save, Eye, EyeOff, CheckCircle, XCircle,
  AlertTriangle, ToggleLeft, ToggleRight, Shield, Globe, TestTube
} from 'lucide-react';
import axios from 'axios';

const PaymentGatewaySettings = () => {
  const [settings, setSettings] = useState({
    razorpay: { is_enabled: false, is_test_mode: true, key_id: '', key_secret: '' },
    stripe: { is_enabled: false, is_test_mode: true, publishable_key: '', secret_key: '' },
    payu: { is_enabled: false, is_test_mode: true, merchant_key: '', merchant_salt: '' },
    default_gateway: 'razorpay',
    manual_payment_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({
    razorpay: false,
    stripe: false,
    payu: false
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/payment-gateway/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Map the response to our state structure
      setSettings({
        razorpay: {
          is_enabled: res.data.razorpay?.is_enabled || false,
          is_test_mode: res.data.razorpay?.is_test_mode ?? true,
          key_id: res.data.razorpay?.key_id || '',
          key_secret: ''  // Never show actual secret
        },
        stripe: {
          is_enabled: res.data.stripe?.is_enabled || false,
          is_test_mode: res.data.stripe?.is_test_mode ?? true,
          publishable_key: res.data.stripe?.publishable_key || '',
          secret_key: ''
        },
        payu: {
          is_enabled: res.data.payu?.is_enabled || false,
          is_test_mode: res.data.payu?.is_test_mode ?? true,
          merchant_key: res.data.payu?.merchant_key || '',
          merchant_salt: ''
        },
        default_gateway: res.data.default_gateway || 'razorpay',
        manual_payment_enabled: res.data.manual_payment_enabled ?? true
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      // Only send non-empty secrets (to avoid overwriting with empty)
      const payload = {
        razorpay: {
          is_enabled: settings.razorpay.is_enabled,
          is_test_mode: settings.razorpay.is_test_mode,
          key_id: settings.razorpay.key_id,
          ...(settings.razorpay.key_secret && { key_secret: settings.razorpay.key_secret })
        },
        stripe: {
          is_enabled: settings.stripe.is_enabled,
          is_test_mode: settings.stripe.is_test_mode,
          publishable_key: settings.stripe.publishable_key,
          ...(settings.stripe.secret_key && { secret_key: settings.stripe.secret_key })
        },
        payu: {
          is_enabled: settings.payu.is_enabled,
          is_test_mode: settings.payu.is_test_mode,
          merchant_key: settings.payu.merchant_key,
          ...(settings.payu.merchant_salt && { merchant_salt: settings.payu.merchant_salt })
        },
        default_gateway: settings.default_gateway,
        manual_payment_enabled: settings.manual_payment_enabled
      };

      await axios.put(`${API_URL}/api/payment-gateway/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Payment gateway settings saved successfully!');
      fetchSettings();  // Refresh to get masked values
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleGateway = (gateway, field) => {
    setSettings(prev => ({
      ...prev,
      [gateway]: {
        ...prev[gateway],
        [field]: !prev[gateway][field]
      }
    }));
  };

  const updateGatewayField = (gateway, field, value) => {
    setSettings(prev => ({
      ...prev,
      [gateway]: {
        ...prev[gateway],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const GatewayCard = ({ name, displayName, logo, config, fields }) => (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden border-2 ${config.is_enabled ? 'border-green-500' : 'border-gray-200'}`}>
      {/* Header */}
      <div className={`p-4 ${config.is_enabled ? 'bg-green-50' : 'bg-gray-50'} border-b flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${config.is_enabled ? 'bg-green-100' : 'bg-gray-200'}`}>
            <CreditCard className={`h-6 w-6 ${config.is_enabled ? 'text-green-600' : 'text-gray-500'}`} />
          </div>
          <div>
            <h3 className="font-bold text-lg">{displayName}</h3>
            <div className="flex items-center gap-2 mt-1">
              {config.is_enabled ? (
                <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                  <CheckCircle className="h-3.5 w-3.5" /> Enabled
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-gray-500 font-medium">
                  <XCircle className="h-3.5 w-3.5" /> Disabled
                </span>
              )}
              {config.is_enabled && (
                <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${config.is_test_mode ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                  <TestTube className="h-3 w-3" />
                  {config.is_test_mode ? 'Test Mode' : 'Live Mode'}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <button
          onClick={() => toggleGateway(name, 'is_enabled')}
          className={`p-2 rounded-lg ${config.is_enabled ? 'text-green-600' : 'text-gray-400'}`}
        >
          {config.is_enabled ? <ToggleRight className="h-8 w-8" /> : <ToggleLeft className="h-8 w-8" />}
        </button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Test/Live Toggle */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span className="text-sm font-medium text-gray-700">Mode</span>
          <div className="flex items-center gap-2">
            <span className={`text-sm ${config.is_test_mode ? 'text-yellow-600 font-medium' : 'text-gray-400'}`}>Test</span>
            <button
              onClick={() => toggleGateway(name, 'is_test_mode')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.is_test_mode ? 'bg-yellow-500' : 'bg-blue-600'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.is_test_mode ? 'translate-x-1' : 'translate-x-6'}`} />
            </button>
            <span className={`text-sm ${!config.is_test_mode ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>Live</span>
          </div>
        </div>

        {/* API Keys */}
        {fields.map((field, idx) => (
          <div key={idx}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
            <div className="relative">
              <input
                type={field.isSecret && !showSecrets[name] ? 'password' : 'text'}
                value={config[field.key] || ''}
                onChange={(e) => updateGatewayField(name, field.key, e.target.value)}
                placeholder={field.placeholder}
                className="w-full px-3 py-2 border rounded-lg pr-10"
              />
              {field.isSecret && (
                <button
                  type="button"
                  onClick={() => setShowSecrets(prev => ({ ...prev, [name]: !prev[name] }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets[name] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            </div>
            {field.hint && <p className="text-xs text-gray-500 mt-1">{field.hint}</p>}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="payment-gateway-settings">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payment Gateway Settings</h1>
          <p className="text-gray-600 mt-1">Configure payment gateways for tenant subscription payments</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="h-5 w-5" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-medium text-yellow-800">Important Security Notice</h3>
          <p className="text-sm text-yellow-700 mt-1">
            Keep your API keys secure. Never share secret keys. Use Test Mode for development and testing.
          </p>
        </div>
      </div>

      {/* General Settings */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          General Settings
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Default Gateway */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Payment Gateway</label>
            <select
              value={settings.default_gateway}
              onChange={(e) => setSettings(prev => ({ ...prev, default_gateway: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="razorpay">Razorpay</option>
              <option value="stripe">Stripe</option>
              <option value="payu">PayU</option>
            </select>
          </div>

          {/* Manual Payment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Manual Payment Options</label>
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={() => setSettings(prev => ({ ...prev, manual_payment_enabled: !prev.manual_payment_enabled }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.manual_payment_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.manual_payment_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-gray-600">
                {settings.manual_payment_enabled ? 'Bank Transfer, UPI (Offline) Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Gateway Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Razorpay */}
        <GatewayCard
          name="razorpay"
          displayName="Razorpay"
          config={settings.razorpay}
          fields={[
            { key: 'key_id', label: 'Key ID', placeholder: 'rzp_test_xxxxx or rzp_live_xxxxx', hint: 'Get from Razorpay Dashboard → Settings → API Keys' },
            { key: 'key_secret', label: 'Key Secret', placeholder: 'Enter secret key', isSecret: true, hint: 'Leave blank to keep existing' }
          ]}
        />

        {/* Stripe */}
        <GatewayCard
          name="stripe"
          displayName="Stripe"
          config={settings.stripe}
          fields={[
            { key: 'publishable_key', label: 'Publishable Key', placeholder: 'pk_test_xxxxx or pk_live_xxxxx', hint: 'Get from Stripe Dashboard → Developers → API Keys' },
            { key: 'secret_key', label: 'Secret Key', placeholder: 'Enter secret key', isSecret: true, hint: 'Leave blank to keep existing' }
          ]}
        />

        {/* PayU */}
        <GatewayCard
          name="payu"
          displayName="PayU"
          config={settings.payu}
          fields={[
            { key: 'merchant_key', label: 'Merchant Key', placeholder: 'Enter merchant key', hint: 'Get from PayU Dashboard' },
            { key: 'merchant_salt', label: 'Merchant Salt', placeholder: 'Enter merchant salt', isSecret: true, hint: 'Leave blank to keep existing' }
          ]}
        />
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 flex items-center gap-2">
            <Globe className="h-4 w-4" /> Razorpay
          </h4>
          <p className="text-sm text-blue-700 mt-1">Best for Indian customers. Supports UPI, Cards, Netbanking, Wallets.</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h4 className="font-medium text-purple-800 flex items-center gap-2">
            <Globe className="h-4 w-4" /> Stripe
          </h4>
          <p className="text-sm text-purple-700 mt-1">Best for international payments. Supports 135+ currencies.</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-medium text-green-800 flex items-center gap-2">
            <Globe className="h-4 w-4" /> PayU
          </h4>
          <p className="text-sm text-green-700 mt-1">Popular in India. Easy integration, multiple payment modes.</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentGatewaySettings;
